/**
 * Plant Loop Store
 *
 * Zustand store for managing plant loop state including loops, branches,
 * nodes, and edges with undo/redo support.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Node, Edge, XYPosition, Connection } from 'reactflow';
import type {
  PlantLoop,
  PlantLoopConfig,
  PlantLoopType,
  PlantLoopSide,
  PlantNodeData,
  PlantEdgeData,
  PlantLoopValidation,
  PlantValidationError,
  PlantValidationWarning,
} from './types';
import { getPlantComponentDefinition } from './components';
import type { FluidMedium } from '../hvac/types';

/**
 * Maximum history size for undo/redo
 */
const MAX_HISTORY_SIZE = 50;

/**
 * Generate unique IDs
 */
function generateId(prefix: string): string {
  return `${prefix}_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a connection node name
 */
function generateNodeName(
  sourceName: string,
  targetName: string,
  _medium: FluidMedium
): string {
  return `${sourceName} to ${targetName} Node`;
}

/**
 * Plant action type for undo/redo
 */
interface PlantAction {
  type: string;
  timestamp: number;
  data: unknown;
  inverse: unknown;
}

interface PlantLoopState {
  /** All plant loops */
  loops: PlantLoop[];

  /** Currently active loop ID */
  activeLoopId: string | null;

  /** React Flow nodes for the active loop */
  nodes: Node<PlantNodeData>[];

  /** React Flow edges for the active loop */
  edges: Edge<PlantEdgeData>[];

  /** Selected node IDs */
  selectedNodeIds: string[];

  /** Selected edge IDs */
  selectedEdgeIds: string[];

  /** Undo history */
  undoStack: PlantAction[];

  /** Redo history */
  redoStack: PlantAction[];

  /** Whether there are unsaved changes */
  isDirty: boolean;

  /** Node counter for unique naming */
  nodeCounter: Map<string, number>;

  // Loop Management Actions

  /** Create a new plant loop */
  createLoop: (type: PlantLoopType, name?: string) => string;

  /** Delete a plant loop */
  deleteLoop: (loopId: string) => void;

  /** Update loop configuration */
  updateLoopConfig: (loopId: string, config: Partial<PlantLoopConfig>) => void;

  /** Set the active loop */
  setActiveLoop: (loopId: string | null) => void;

  /** Get a loop by ID */
  getLoop: (loopId: string) => PlantLoop | undefined;

  /** Get the active loop */
  getActiveLoop: () => PlantLoop | undefined;

  // Node Management Actions

  /** Add a node to the active loop */
  addNode: (
    componentType: string,
    position: XYPosition,
    loopSide: PlantLoopSide,
    name?: string
  ) => string;

  /** Remove a node */
  removeNode: (nodeId: string) => void;

  /** Update node position */
  updateNodePosition: (nodeId: string, position: XYPosition) => void;

  /** Update node data */
  updateNodeData: (nodeId: string, data: Partial<PlantNodeData>) => void;

  /** Update multiple node positions */
  updateNodePositions: (updates: Array<{ id: string; position: XYPosition }>) => void;

  // Edge Management Actions

  /** Add an edge */
  addEdge: (connection: Connection, loopSide: PlantLoopSide) => string | null;

  /** Remove an edge */
  removeEdge: (edgeId: string) => void;

  /** Validate a potential connection */
  validateConnection: (connection: Connection) => { valid: boolean; reason?: string };

  // Selection Actions

  /** Select nodes */
  selectNodes: (nodeIds: string[]) => void;

  /** Select edges */
  selectEdges: (edgeIds: string[]) => void;

  /** Clear selection */
  clearSelection: () => void;

  /** Delete selected elements */
  deleteSelected: () => void;

  // History Actions

  /** Undo last action */
  undo: () => void;

  /** Redo last undone action */
  redo: () => void;

  /** Check if undo is available */
  canUndo: () => boolean;

  /** Check if redo is available */
  canRedo: () => boolean;

  // State Management

  /** Clear all data */
  clear: () => void;

  /** Mark as clean (saved) */
  markClean: () => void;

  /** Set nodes (for React Flow callbacks) */
  setNodes: (nodes: Node<PlantNodeData>[]) => void;

  /** Set edges (for React Flow callbacks) */
  setEdges: (edges: Edge<PlantEdgeData>[]) => void;

  /** Get node by ID */
  getNode: (nodeId: string) => Node<PlantNodeData> | undefined;

  /** Get edge by ID */
  getEdge: (edgeId: string) => Edge<PlantEdgeData> | undefined;

  // Validation

  /** Validate the active loop */
  validateActiveLoop: () => PlantLoopValidation;

  /** Get nodes by loop side */
  getNodesBySide: (side: PlantLoopSide) => Node<PlantNodeData>[];

  /** Get edges by loop side */
  getEdgesBySide: (side: PlantLoopSide) => Edge<PlantEdgeData>[];
}

/**
 * Push an action to the undo stack
 */
function pushUndo(state: PlantLoopState, action: PlantAction): Partial<PlantLoopState> {
  const undoStack = [...state.undoStack, action];
  if (undoStack.length > MAX_HISTORY_SIZE) {
    undoStack.shift();
  }
  return {
    undoStack,
    redoStack: [],
    isDirty: true,
  };
}

/**
 * Get edge color based on loop type
 */
function getLoopEdgeColor(loopType: PlantLoopType | undefined): string {
  switch (loopType) {
    case 'chilled-water':
      return '#1E88E5';
    case 'hot-water':
      return '#E53935';
    case 'condenser-water':
      return '#8E24AA';
    case 'ground-loop':
      return '#43A047';
    case 'service-hot-water':
      return '#FB8C00';
    default:
      return '#78909C';
  }
}

export const usePlantLoopStore = create<PlantLoopState>()(
  devtools(
    (set, get) => ({
      loops: [],
      activeLoopId: null,
      nodes: [],
      edges: [],
      selectedNodeIds: [],
      selectedEdgeIds: [],
      undoStack: [],
      redoStack: [],
      isDirty: false,
      nodeCounter: new Map(),

      createLoop: (type: PlantLoopType, customName?: string) => {
        const id = generateId('loop');
        const loopDefaults = {
          'chilled-water': {
            fluidType: 'Water',
            designExitTemp: 6.67,
            designDeltaT: 5.56,
            loadDistribution: 'Optimal' as const,
          },
          'hot-water': {
            fluidType: 'Water',
            designExitTemp: 82.2,
            designDeltaT: 11.1,
            loadDistribution: 'Optimal' as const,
          },
          'condenser-water': {
            fluidType: 'Water',
            designExitTemp: 29.4,
            designDeltaT: 5.56,
            loadDistribution: 'SequentialLoad' as const,
          },
          'ground-loop': {
            fluidType: 'PropyleneGlycol',
            designExitTemp: 15.6,
            designDeltaT: 5.56,
            loadDistribution: 'Optimal' as const,
          },
          'service-hot-water': {
            fluidType: 'Water',
            designExitTemp: 60,
            designDeltaT: 16.7,
            loadDistribution: 'SequentialLoad' as const,
          },
        };

        const defaults = loopDefaults[type];
        const loopTypeName = {
          'chilled-water': 'Chilled Water Loop',
          'hot-water': 'Hot Water Loop',
          'condenser-water': 'Condenser Water Loop',
          'ground-loop': 'Ground Loop',
          'service-hot-water': 'Service Hot Water Loop',
        };

        const newLoop: PlantLoop = {
          id,
          config: {
            name: customName || loopTypeName[type],
            type,
            fluidType: defaults.fluidType,
            designExitTemp: defaults.designExitTemp,
            designDeltaT: defaults.designDeltaT,
            minFlowRate: 'autosize',
            maxFlowRate: 'autosize',
            loadDistribution: defaults.loadDistribution,
          },
          supplyBranches: [],
          demandBranches: [],
          supplyInletNode: `${customName || loopTypeName[type]} Supply Inlet Node`,
          supplyOutletNode: `${customName || loopTypeName[type]} Supply Outlet Node`,
          demandInletNode: `${customName || loopTypeName[type]} Demand Inlet Node`,
          demandOutletNode: `${customName || loopTypeName[type]} Demand Outlet Node`,
        };

        set((state) => ({
          loops: [...state.loops, newLoop],
          activeLoopId: id,
          nodes: [],
          edges: [],
          isDirty: true,
        }));

        return id;
      },

      deleteLoop: (loopId: string) => {
        set((state) => {
          const newLoops = state.loops.filter((l) => l.id !== loopId);
          const newActiveId =
            state.activeLoopId === loopId
              ? newLoops.length > 0
                ? newLoops[0]?.id ?? null
                : null
              : state.activeLoopId;

          return {
            loops: newLoops,
            activeLoopId: newActiveId,
            nodes: newActiveId === state.activeLoopId ? state.nodes : [],
            edges: newActiveId === state.activeLoopId ? state.edges : [],
            isDirty: true,
          };
        });
      },

      updateLoopConfig: (loopId: string, configUpdates: Partial<PlantLoopConfig>) => {
        set((state) => ({
          loops: state.loops.map((loop) =>
            loop.id === loopId
              ? { ...loop, config: { ...loop.config, ...configUpdates } }
              : loop
          ),
          isDirty: true,
        }));
      },

      setActiveLoop: (loopId: string | null) => {
        set({ activeLoopId: loopId, nodes: [], edges: [] });
      },

      getLoop: (loopId: string) => {
        return get().loops.find((l) => l.id === loopId);
      },

      getActiveLoop: () => {
        const { loops, activeLoopId } = get();
        return loops.find((l) => l.id === activeLoopId);
      },

      addNode: (
        componentType: string,
        position: XYPosition,
        loopSide: PlantLoopSide,
        customName?: string
      ) => {
        const definition = getPlantComponentDefinition(componentType);
        if (!definition) {
          console.error(`Unknown plant component type: ${componentType}`);
          return '';
        }

        const id = generateId('node');
        const state = get();
        const counter = (state.nodeCounter.get(componentType) || 0) + 1;
        const name = customName || `${definition.displayName} ${String(counter)}`;

        const newNode: Node<PlantNodeData> = {
          id,
          type: 'plantNode',
          position,
          data: {
            componentType,
            name,
            loopSide,
            branchIndex: 0,
            fields: { ...definition.defaults },
            hasErrors: false,
            errors: [],
          },
        };

        const action: PlantAction = {
          type: 'ADD_NODE',
          timestamp: Date.now(),
          data: newNode,
          inverse: { nodeId: id },
        };

        set((state) => ({
          nodes: [...state.nodes, newNode],
          nodeCounter: new Map(state.nodeCounter).set(componentType, counter),
          ...pushUndo(state, action),
        }));

        return id;
      },

      removeNode: (nodeId: string) => {
        const state = get();
        const node = state.nodes.find((n) => n.id === nodeId);
        if (!node) return;

        const connectedEdges = state.edges.filter(
          (e) => e.source === nodeId || e.target === nodeId
        );

        const action: PlantAction = {
          type: 'REMOVE_NODE',
          timestamp: Date.now(),
          data: { nodeId },
          inverse: { node, edges: connectedEdges },
        };

        set((state) => ({
          nodes: state.nodes.filter((n) => n.id !== nodeId),
          edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
          selectedNodeIds: state.selectedNodeIds.filter((id) => id !== nodeId),
          ...pushUndo(state, action),
        }));
      },

      updateNodePosition: (nodeId: string, position: XYPosition) => {
        set((state) => ({
          nodes: state.nodes.map((n) => (n.id === nodeId ? { ...n, position } : n)),
          isDirty: true,
        }));
      },

      updateNodeData: (nodeId: string, dataUpdates: Partial<PlantNodeData>) => {
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === nodeId ? { ...n, data: { ...n.data, ...dataUpdates } } : n
          ),
          isDirty: true,
        }));
      },

      updateNodePositions: (updates: Array<{ id: string; position: XYPosition }>) => {
        set((state) => ({
          nodes: state.nodes.map((n) => {
            const update = updates.find((u) => u.id === n.id);
            return update ? { ...n, position: update.position } : n;
          }),
          isDirty: true,
        }));
      },

      addEdge: (connection: Connection, loopSide: PlantLoopSide) => {
        const validation = get().validateConnection(connection);
        if (!validation.valid) {
          console.warn(`Invalid connection: ${validation.reason ?? 'unknown reason'}`);
          return null;
        }

        if (!connection.source || !connection.target || !connection.sourceHandle || !connection.targetHandle) {
          return null;
        }

        const state = get();
        const sourceNode = state.nodes.find((n) => n.id === connection.source);
        const targetNode = state.nodes.find((n) => n.id === connection.target);

        if (!sourceNode || !targetNode) return null;

        const sourceDef = getPlantComponentDefinition(sourceNode.data.componentType);
        const sourcePort = sourceDef?.ports.find((p) => p.id === connection.sourceHandle);

        if (!sourcePort) return null;

        const activeLoop = get().getActiveLoop();
        const id = generateId('edge');
        const nodeName = generateNodeName(
          sourceNode.data.name,
          targetNode.data.name,
          sourcePort.medium
        );

        const newEdge: Edge<PlantEdgeData> = {
          id,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: getLoopEdgeColor(activeLoop?.config.type),
            strokeWidth: 2,
          },
          data: {
            sourcePort: connection.sourceHandle,
            targetPort: connection.targetHandle,
            medium: sourcePort.medium,
            nodeName,
            loopSide,
          },
        };

        const action: PlantAction = {
          type: 'ADD_EDGE',
          timestamp: Date.now(),
          data: newEdge,
          inverse: { edgeId: id },
        };

        set((state) => ({
          edges: [...state.edges, newEdge],
          ...pushUndo(state, action),
        }));

        return id;
      },

      removeEdge: (edgeId: string) => {
        const edge = get().edges.find((e) => e.id === edgeId);
        if (!edge) return;

        const action: PlantAction = {
          type: 'REMOVE_EDGE',
          timestamp: Date.now(),
          data: { edgeId },
          inverse: { edge },
        };

        set((state) => ({
          edges: state.edges.filter((e) => e.id !== edgeId),
          selectedEdgeIds: state.selectedEdgeIds.filter((id) => id !== edgeId),
          ...pushUndo(state, action),
        }));
      },

      validateConnection: (connection: Connection) => {
        const state = get();

        const sourceNode = state.nodes.find((n) => n.id === connection.source);
        const targetNode = state.nodes.find((n) => n.id === connection.target);

        if (!sourceNode || !targetNode) {
          return { valid: false, reason: 'Source or target node not found' };
        }

        const sourceDef = getPlantComponentDefinition(sourceNode.data.componentType);
        const targetDef = getPlantComponentDefinition(targetNode.data.componentType);

        if (!sourceDef || !targetDef) {
          return { valid: false, reason: 'Component definition not found' };
        }

        const sourcePort = sourceDef.ports.find((p) => p.id === connection.sourceHandle);
        const targetPort = targetDef.ports.find((p) => p.id === connection.targetHandle);

        if (!sourcePort || !targetPort) {
          return { valid: false, reason: 'Port not found' };
        }

        if (sourcePort.direction !== 'outlet') {
          return { valid: false, reason: 'Source must be an outlet port' };
        }

        if (targetPort.direction !== 'inlet') {
          return { valid: false, reason: 'Target must be an inlet port' };
        }

        if (sourcePort.medium !== targetPort.medium) {
          return {
            valid: false,
            reason: `Medium mismatch: ${sourcePort.medium} cannot connect to ${targetPort.medium}`,
          };
        }

        const existingConnection = state.edges.find(
          (e) => e.target === connection.target && e.targetHandle === connection.targetHandle
        );

        if (existingConnection) {
          return { valid: false, reason: 'Target port already has a connection' };
        }

        if (connection.source === connection.target) {
          return { valid: false, reason: 'Cannot connect a node to itself' };
        }

        return { valid: true };
      },

      selectNodes: (nodeIds: string[]) => {
        set({ selectedNodeIds: nodeIds });
      },

      selectEdges: (edgeIds: string[]) => {
        set({ selectedEdgeIds: edgeIds });
      },

      clearSelection: () => {
        set({ selectedNodeIds: [], selectedEdgeIds: [] });
      },

      deleteSelected: () => {
        const state = get();

        for (const edgeId of state.selectedEdgeIds) {
          get().removeEdge(edgeId);
        }

        for (const nodeId of state.selectedNodeIds) {
          get().removeNode(nodeId);
        }
      },

      undo: () => {
        const state = get();
        if (state.undoStack.length === 0) return;

        const action = state.undoStack[state.undoStack.length - 1];
        if (!action) return;
        const inverse = action.inverse as Record<string, unknown>;

        switch (action.type) {
          case 'ADD_NODE':
            set((s) => ({
              nodes: s.nodes.filter((n) => n.id !== (inverse['nodeId'] as string)),
            }));
            break;

          case 'REMOVE_NODE':
            set((s) => ({
              nodes: [...s.nodes, inverse['node'] as Node<PlantNodeData>],
              edges: [...s.edges, ...(inverse['edges'] as Edge<PlantEdgeData>[])],
            }));
            break;

          case 'ADD_EDGE':
            set((s) => ({
              edges: s.edges.filter((e) => e.id !== (inverse['edgeId'] as string)),
            }));
            break;

          case 'REMOVE_EDGE':
            set((s) => ({
              edges: [...s.edges, inverse['edge'] as Edge<PlantEdgeData>],
            }));
            break;
        }

        set((s) => ({
          undoStack: s.undoStack.slice(0, -1),
          redoStack: [...s.redoStack, action],
          isDirty: true,
        }));
      },

      redo: () => {
        const state = get();
        if (state.redoStack.length === 0) return;

        const action = state.redoStack[state.redoStack.length - 1];
        if (!action) return;
        const data = action.data as Record<string, unknown>;

        switch (action.type) {
          case 'ADD_NODE':
            set((s) => ({
              nodes: [...s.nodes, data as unknown as Node<PlantNodeData>],
            }));
            break;

          case 'REMOVE_NODE':
            set((s) => ({
              nodes: s.nodes.filter((n) => n.id !== (data['nodeId'] as string)),
              edges: s.edges.filter(
                (e) => e.source !== (data['nodeId'] as string) && e.target !== (data['nodeId'] as string)
              ),
            }));
            break;

          case 'ADD_EDGE':
            set((s) => ({
              edges: [...s.edges, data as unknown as Edge<PlantEdgeData>],
            }));
            break;

          case 'REMOVE_EDGE':
            set((s) => ({
              edges: s.edges.filter((e) => e.id !== (data['edgeId'] as string)),
            }));
            break;
        }

        set((s) => ({
          redoStack: s.redoStack.slice(0, -1),
          undoStack: [...s.undoStack, action],
          isDirty: true,
        }));
      },

      canUndo: () => get().undoStack.length > 0,

      canRedo: () => get().redoStack.length > 0,

      clear: () => {
        set({
          loops: [],
          activeLoopId: null,
          nodes: [],
          edges: [],
          selectedNodeIds: [],
          selectedEdgeIds: [],
          undoStack: [],
          redoStack: [],
          isDirty: false,
          nodeCounter: new Map(),
        });
      },

      markClean: () => {
        set({ isDirty: false });
      },

      setNodes: (nodes: Node<PlantNodeData>[]) => {
        set({ nodes, isDirty: true });
      },

      setEdges: (edges: Edge<PlantEdgeData>[]) => {
        set({ edges, isDirty: true });
      },

      getNode: (nodeId: string) => {
        return get().nodes.find((n) => n.id === nodeId);
      },

      getEdge: (edgeId: string) => {
        return get().edges.find((e) => e.id === edgeId);
      },

      validateActiveLoop: (): PlantLoopValidation => {
        const state = get();
        const activeLoop = state.getActiveLoop();
        const errors: PlantValidationError[] = [];
        const warnings: PlantValidationWarning[] = [];

        if (!activeLoop) {
          return { valid: false, errors: [{ code: 'NO_ACTIVE_LOOP', message: 'No active loop selected' }], warnings: [] };
        }

        const supplyNodes = state.getNodesBySide('supply');
        const demandNodes = state.getNodesBySide('demand');

        // Check for pump on supply side
        const hasPump = supplyNodes.some((n) => {
          const def = getPlantComponentDefinition(n.data.componentType);
          return def?.category === 'pumps';
        });

        if (!hasPump) {
          errors.push({
            code: 'NO_PUMP',
            message: 'Supply side must have at least one pump',
            loopSide: 'supply',
          });
        }

        // Check for supply equipment
        const hasSupplyEquipment = supplyNodes.some((n) => {
          const def = getPlantComponentDefinition(n.data.componentType);
          return def?.supplyEquipment && def.category !== 'pumps' && def.category !== 'distribution';
        });

        if (!hasSupplyEquipment) {
          errors.push({
            code: 'NO_SUPPLY_EQUIPMENT',
            message: 'Supply side must have at least one piece of equipment (chiller, boiler, tower, etc.)',
            loopSide: 'supply',
          });
        }

        // Check for demand equipment
        const hasDemandEquipment = demandNodes.length > 0;

        if (!hasDemandEquipment) {
          warnings.push({
            code: 'NO_DEMAND_EQUIPMENT',
            message: 'Demand side has no equipment',
            suggestion: 'Add coils or other demand-side equipment',
          });
        }

        // Check for unconnected nodes
        for (const node of [...supplyNodes, ...demandNodes]) {
          const hasInlet = state.edges.some((e) => e.target === node.id);
          const hasOutlet = state.edges.some((e) => e.source === node.id);

          if (!hasInlet && !hasOutlet) {
            warnings.push({
              code: 'UNCONNECTED_NODE',
              message: `${node.data.name} is not connected to any other component`,
              nodeId: node.id,
              suggestion: 'Connect this component to complete the loop',
            });
          }
        }

        return {
          valid: errors.length === 0,
          errors,
          warnings,
        };
      },

      getNodesBySide: (side: PlantLoopSide) => {
        return get().nodes.filter((n) => n.data.loopSide === side);
      },

      getEdgesBySide: (side: PlantLoopSide) => {
        return get().edges.filter((e) => e.data?.loopSide === side);
      },
    }),
    { name: 'PlantLoopStore' }
  )
);

/**
 * Selector for supply side nodes
 */
export const useSupplyNodes = () => {
  return usePlantLoopStore((state) => state.getNodesBySide('supply'));
};

/**
 * Selector for demand side nodes
 */
export const useDemandNodes = () => {
  return usePlantLoopStore((state) => state.getNodesBySide('demand'));
};

/**
 * Selector for active loop
 */
export const useActiveLoop = () => {
  return usePlantLoopStore((state) => state.getActiveLoop());
};
